"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Counter from "@/app/shared-components/counter";
import {
  LIFESTYLE_COUNTDOWN_ID,
  clearLifestyleCountdownHeight,
  syncLifestyleCountdownHeight,
} from "../lifestyle-sticky";

interface CompetitionCountdownProps {
  endDateTime: string;
}

/**
 * Competition countdown strip — sticky under the site header on /prizes/lifestyle.
 *
 * Mobile ([Figma](https://www.figma.com/design/RmIvqUHFkF0qSjcpoIn0c0/BOTB-updates-2026?node-id=16170-72466&m=dev)):
 * full-width black bar, Roboto 12px, centred "Ends in" + timer.
 *
 * Desktop ([Figma](https://www.figma.com/design/RmIvqUHFkF0qSjcpoIn0c0/BOTB-updates-2026?node-id=15390-11394&m=dev)):
 * 48px #121212 track (max 1360px), Roboto 16px, 10px gap between label and digits.
 *
 * Publishes `--lifestyle-countdown-height` so the filter bar stacks beneath it.
 */
export default function CompetitionCountdown({ endDateTime }: CompetitionCountdownProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const endDate = useMemo(() => new Date(endDateTime), [endDateTime]);
  const isValidEndDate = !Number.isNaN(endDate.getTime());
  const [isActive, setIsActive] = useState(() => isValidEndDate && endDate.getTime() > Date.now());

  useEffect(() => {
    if (!isValidEndDate) {
      setIsActive(false);
      return;
    }

    const remainingMs = endDate.getTime() - Date.now();
    if (remainingMs <= 0) {
      setIsActive(false);
      return;
    }

    setIsActive(true);
    const timeoutId = window.setTimeout(() => setIsActive(false), remainingMs);
    return () => window.clearTimeout(timeoutId);
  }, [endDate, isValidEndDate]);

  useEffect(() => {
    if (!isActive) {
      clearLifestyleCountdownHeight();
      return;
    }

    syncLifestyleCountdownHeight();

    const bar = barRef.current;
    if (!bar) {
      clearLifestyleCountdownHeight();
      return;
    }

    const observer = new ResizeObserver(syncLifestyleCountdownHeight);
    observer.observe(bar);
    window.addEventListener("resize", syncLifestyleCountdownHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncLifestyleCountdownHeight);
      clearLifestyleCountdownHeight();
    };
  }, [isActive]);

  if (!isValidEndDate || !isActive) return null;

  return (
    <div
      ref={barRef}
      id={LIFESTYLE_COUNTDOWN_ID}
      style={{ top: "var(--header-height)" }}
      className="sticky z-[1002] flex items-center justify-center w-full min-h-6 lg:min-h-12 px-0"
      role="timer"
      aria-live="polite"
      aria-label="Competition ends in">
      <div className="flex items-center justify-center w-full min-h-6 lg:h-12 py-1 lg:py-0 bg-black lg:bg-[#121212]">
        <Counter
          EndDateTime={endDate}
          textClass="font-roboto text-xs lg:text-base font-normal leading-4 lg:leading-6 text-[#BCBCC0] lg:text-[#AAAAAA] whitespace-nowrap"
          numericClass="font-roboto text-xs lg:text-base font-semibold lg:font-medium leading-4 lg:leading-6 text-primary"
          containerClass="flex items-center justify-center gap-1 lg:gap-2.5"
          format="d:h:m:s"
        />
      </div>
    </div>
  );
}
